import { useEffect, useRef, useState, useMemo } from "react";
import Button from "../../../components/button/button";
import Modal from "../../../components/modal/modal";
import Input from "../../../components/inputs/input/input";
import InputDropdown from "../../../components/inputs/inputDropdown/inputDropdown";
import InputDropdownAdd from "../../../components/inputs/inputDropdownAdd/inputDropdownAdd";
import Notification from "../../../components/notification/notification";
import TableEditable from "../../../components/tableEditable/tableEditable";
import { BiImport } from "react-icons/bi";
import { getApiInstance } from "../../../utils/api";
import Badge from "../../../components/badge/badge";
import { BiExport } from "react-icons/bi";
import { searchByKeywords } from "../../../utils/searchUtils";
import InputSearch from "../../../components/inputs/inputSearch/inputSearch";
import InputTextfield from "../../../components/inputs/inputTextfield/inputTextfield";
import FileList from "../../../components/fileList/fileList";
import { PiWarning } from "react-icons/pi";
import { IoMdClose } from "react-icons/io";
import { FiFilter } from "react-icons/fi";
import Accordion from "../../../components/accordion/accordion";
import Tag from "../../../components/tag/tag";
import "./validacionGOIProveedor.scss";
import { useLocation } from "react-router-dom";


// Helper para formatear fecha de comentarios
const formatDateComment = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit", 
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Definición de estados para el filtro
const estados = [
  { id: 1, label: "Coincidencia" },
  { id: 2, label: "Sin coincidencia" },
  { id: 3, label: "No encontrado" },
  { id: 4, label: "Sin precio" },
];

export default function ValidacionGOIProveedor({ user }) {
  const notificationRef = useRef(null);
  const { pathname } = useLocation();

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [nombreTarifaGoi, setNombreTarifaGoi] = useState("");
  const [tarifasProveedorOptions, setTarifasProveedorOptions] = useState([]);
  const [tarifasGoiExistentes, setTarifasGoiExistentes] = useState([]);
  const [selectedTarifaBook, setSelectedTarifaBook] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [isImportSubmitting, setIsImportSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Estados para el sistema de comentarios
  const [comentariosPorFila, setComentariosPorFila] = useState({});
  const [isComentariosModalOpen, setIsComentariosModalOpen] = useState(false);
  const [comentarioModalRow, setComentarioModalRow] = useState(null);
  const [comentarioModalInput, setComentarioModalInput] = useState("");
  const [comentarioModalFiles, setComentarioModalFiles] = useState([]);
  const [comentarioModalInputFileText, setComentarioModalInputFileText] = useState("Selecciona un archivo");

  // Estados para el sistema de filtros
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [filterEstado, setFilterEstado] = useState([]);
  const [filterUnidad, setFilterUnidad] = useState([]);
  const [filterFechaImportacion, setFilterFechaImportacion] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [fechasImportacion, setFechasImportacion] = useState([]);

  // tabla mostrada: sólo "importado" (comparativa / detalle)
  const [comparativaGOI, setComparativaGOI] = useState([]);

  // importaciones / detalle: para mostrar "importado" por defecto
  const [importaciones, setImportaciones] = useState([]);
  const [filteredComparativaGOI, setFilteredComparativaGOI] = useState([]);
  const [selectedImportacionId, setSelectedImportacionId] = useState(null);
  const [selectedComparativaGOI, setSelectedComparativaGOI] = useState([]);
  const [importacion, setImportacion] = useState(null);

  // confirm modal - cambiar a popup
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [importConfirmData, setImportConfirmData] = useState(null);

  // Nuevo: estado para el modal de duplicadas
  const [duplicadasModalOpen, setDuplicadasModalOpen] = useState(false);
  const [duplicadasModalData, setDuplicadasModalData] = useState([]);

  // Función para manejar selección y adición de tarifas GOI
  const handleSetNombreTarifaGoi = (e, type = "select") => {
    // 1) Selección de un elemento existente: tipo "select"
    if (type === "select") {
      setNombreTarifaGoi(e);
      return;
    }

    // 2) Añadir uno nuevo: tipo "add"
    if (type === "add") {
      // En este componente no añadimos fecha automáticamente
      // Solo usamos el nombre tal como lo escribió el usuario
      const newName = e;

      // Actualizamos el dropdown
      setTarifasGoiExistentes((old) => [...old, { label: newName, value: newName }]);

      // Seleccionamos ese nuevo
      setNombreTarifaGoi(newName);
      return;
    }
  };

  // helpers
  const isFueraAprobado = (row) =>
    Boolean(
      row?.AprobadoFueraDeBook ??
        row?.aprobadoFueraDeBook ??
        row?.AprobadoFueraDeBook === 1 ??
        row?.aprobadoEspecial
    );

  // Función para abrir modal de comentarios
  const handleOpenComentariosModal = (row) => {
    setComentarioModalRow(row);
    setComentarioModalInput("");
    setComentarioModalFiles([]);
    setIsComentariosModalOpen(true);
  };

  // columnas reducidas para proveedores (sin Imagen, sin Precio Unidad, sin acciones)
  const columnsProveedor = [
    {
      key: "estado",
      label: "Estado",
      render: (row) => {
        // Si es NoEncontrado pero aprobado fuera del Book, mostrar chip verde distinto
        if (row?.estado === 3 && isFueraAprobado(row)) {
          return <Badge label="Fuera de Book (aprobado)" variant="success" />;
        }

        const labels = { 1: "Coincidencia", 2: "Sin coincidencia", 3: "No encontrado", 4: "Sin precio" };
        let variant;
        switch (row.estado) {
          case 1:
            variant = "success";
            break;
          case 2:
            variant = "danger";
            break;
          case 3:
            variant = "info";
            break;
          case 4:
            variant = "warning";
            break;
          default:
            variant = "default";
        }
        return <Badge label={labels[row.estado] || "Sin estado"} variant={variant} />;
      },
      editable: false,
    },
    { key: "referencia", label: "Referencia", editable: false },
    { key: "descripcion", label: "Descripción", editable: false },
    {
      key: "precioGOI",
      label: "Precio GOI",
      type: "number",
      editable: false,
      render: (row) => {
        if (row.precioGOI === 0 || row.precioGOI === null || row.precioGOI === "undefined") {
          return "-";
        }
        
        const precioGOI = parseFloat(row.precioGOI);
        const precioBook = parseFloat(row.precioBook);
        const isMatch = !isNaN(precioGOI) && !isNaN(precioBook) && Math.abs(precioGOI - precioBook) < 0.01;
        
        return (
          <span style={{
            color: isMatch ? '#28a745' : '#dc3545',
            fontWeight: isMatch ? '600' : '500',
            textDecoration: isMatch ? 'none' : 'underline',
            textDecorationStyle: isMatch ? 'none' : 'wavy'
          }}>
            {row.precioGOI}
          </span>
        );
      },
    },
    {
      key: "unidad",
      label: "Unidad GOI",
      type: "number",
      editable: false,
      render: (row) => {
        if (row.unidad === 0 || row.unidad === null) {
          return "-";
        }
        
        const unidadGOI = row.unidad?.toString()?.trim();
        const unidadBook = row.unidadBOOK?.toString()?.trim();
        const isMatch = unidadGOI && unidadBook && unidadGOI === unidadBook;
        
        return (
          <span style={{
            color: isMatch ? '#28a745' : '#dc3545',
            fontWeight: isMatch ? '600' : '500',
            textDecoration: isMatch ? 'none' : 'underline',
            textDecorationStyle: isMatch ? 'none' : 'wavy'
          }}>
            {row.unidad}
          </span>
        );
      },
    },
    {
      key: "precioBook",
      label: "Precio Book",
      type: "number",
      editable: false,
      render: (row) => {
        if (row.precioBook === 0 || row.precioBook === null) {
          return "-";
        }
        
        const precioGOI = parseFloat(row.precioGOI);
        const precioBook = parseFloat(row.precioBook);
        const isMatch = !isNaN(precioGOI) && !isNaN(precioBook) && Math.abs(precioGOI - precioBook) < 0.01;
        
        return (
          <span style={{
            color: '#28a745',
            fontWeight: '600',
            backgroundColor: isMatch ? 'rgba(40, 167, 69, 0.1)' : 'transparent',
            padding: isMatch ? '2px 4px' : '0',
            borderRadius: isMatch ? '3px' : '0'
          }}>
            {row.precioBook}
          </span>
        );
      },
    },
    {
      key: "unidadBOOK",
      label: "Unidad Book",
      type: "number",
      editable: false,
      render: (row) => {
        if (row.unidadBOOK === 0 || row.unidadBOOK === null) {
          return "-";
        }
        
        const unidadGOI = row.unidad?.toString()?.trim();
        const unidadBook = row.unidadBOOK?.toString()?.trim();
        const isMatch = unidadGOI && unidadBook && unidadGOI === unidadBook;
        
        return (
          <span style={{
            color: '#28a745',
            fontWeight: '600',
            backgroundColor: isMatch ? 'rgba(40, 167, 69, 0.1)' : 'transparent',
            padding: isMatch ? '2px 4px' : '0',
            borderRadius: isMatch ? '3px' : '0'
          }}>
            {row.unidadBOOK}
          </span>
        );
      },
    },
    {
      key: "fechaImportacion",
      label: "Fecha importación",
      editable: false,
      render: (row) =>
        row.fechaImportacion
          ? new Date(row.fechaImportacion).toLocaleDateString("es-ES", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })
          : "-",
    },
    {
      key: "verComentarios",
      label: "Comentarios",
      render: (row) => (
        <Button
          label="Ver comentarios"
          variant="outlined"
          onClick={() => handleOpenComentariosModal(row)}
        />
      ),
      editable: false,
    },
  ];

  useEffect(() => {
    // precargar tarifas proveedor para el modal
    const api = getApiInstance();
    const fetchTarifasProveedor = async () => {
      try {
        const res = await api.get("/api/GOI/Tarifas/Proveedor");
        const options = (res.data || []).map((t) => ({
          label: t.nombre || t.nombreTarifa || t,
          value: t.nombre || t.nombreTarifa || t,
        }));
        setTarifasProveedorOptions(options);
      } catch (err) {
        notificationRef.current?.addNotification("Error al cargar tarifas proveedor", "error");
      }
    };
    fetchTarifasProveedor();
    
    // Función para obtener tarifas GOI existentes
    const fetchTarifasGoiExistentes = async () => {
      try {
        const res = await api.get("/api/GOI/Importaciones", {
          params: { tipo: "Tarifa", top: 100 },
        });
        const raw = Array.isArray(res.data) ? res.data : [];
        const tarifasUnicas = [...new Set(
          raw
            .map(item => item.nombreTarifaGoi ?? item.NombreTarifaGoi)
            .filter(nombre => nombre && nombre.trim())
        )];
        const options = tarifasUnicas.map(tarifa => ({
          label: tarifa,
          value: tarifa,
        }));
        setTarifasGoiExistentes(options);
      } catch (err) {
        console.warn("Error al cargar tarifas GOI existentes", err);
        setTarifasGoiExistentes([]);
      }
    };
    fetchTarifasGoiExistentes();
    
    // además, al montar intentamos cargar la última importación abierta y su detalle
    const initLoadImportado = async () => {
      try {
        const res = await api.get("/api/GOI/Importaciones", {
          params: { tipo: "Tarifa", cerrada: false, top: 100 },
        });
        const raw = Array.isArray(res.data) ? res.data : [];
        const normalized = raw.map((item) => ({
          IdImportacion: item.idImportacion ?? item.IdImportacion ?? item.Id ?? null,
          ArchivoNombre: item.archivoNombre ?? item.ArchivoNombre ?? item.archivoNombre ?? null,
          NombreTarifaGoi: item.nombreTarifaGoi ?? item.NombreTarifaGoi ?? null,
          FechaSubida: item.fechaSubida ?? item.FechaSubida ?? null,
          Cerrada: item.cerrada ?? item.Cerrada ?? false,
        }));
        setImportaciones(normalized);
        const firstOpen = normalized.find((imp) => !imp.Cerrada);
        firstOpen && setImportacion(firstOpen);
        if (firstOpen?.IdImportacion) {
          setSelectedImportacionId(firstOpen.IdImportacion);
          // cargar detalle asociado y mostrarlo por defecto
          const det = await api.get(`/api/GOI/Importaciones/${firstOpen.IdImportacion}/detalle`);
          const detalleArray = Array.isArray(det.data) ? det.data : [];
          setComparativaGOI(detalleArray);
          setFilteredComparativaGOI(detalleArray)
        }
      } catch (err) {
        // no bloqueante: dejamos la tabla vacía si falla
        // console.warn("No se pudo cargar importado por defecto", err);
      }
    };
    initLoadImportado();
  }, []);

  // Nuevo: función reutilizable para cargar importaciones abiertas
  const fetchImportaciones = async () => {
    try {
      const api = getApiInstance();
      const res = await api.get("/api/GOI/Importaciones", {
        params: { tipo: "Tarifa", cerrada: false, top: 100 },
      });
      const raw = Array.isArray(res.data) ? res.data : [];
      const normalized = raw.map((item) => ({
        IdImportacion: item.idImportacion ?? item.IdImportacion ?? item.Id ?? null,
        ArchivoNombre: item.archivoNombre ?? item.ArchivoNombre ?? item.archivoNombre ?? null,
        NombreTarifaGoi: item.nombreTarifaGoi ?? item.NombreTarifaGoi ?? null,
        FechaSubida: item.fechaSubida ?? item.FechaSubida ?? null,
        Cerrada: item.cerrada ?? item.Cerrada ?? false,
      }));
      setImportaciones(normalized);
      return normalized;
    } catch (err) {
      notificationRef.current?.addNotification("Error al cargar importaciones", "error");
      return [];
    }
  };

  // Nuevo: cargar detalle de una importación concreta
  const fetchImportacionDetalle = async (id) => {
    if (!id) return;
    try {
      const api = getApiInstance();
      const res = await api.get(`/api/GOI/Importaciones/${id}/detalle`);
      const detalleArray = Array.isArray(res.data) ? res.data : [];
      setComparativaGOI(detalleArray);
      setFilteredComparativaGOI(detalleArray);
      setSelectedImportacionId(id);
      
      // Actualizar el estado importacion con los datos completos
      const importacionSeleccionada = importaciones.find(imp => imp.IdImportacion === id);
      if (importacionSeleccionada) {
        setImportacion(importacionSeleccionada);
      }
    } catch (err) {
      notificationRef.current?.addNotification("Error al cargar detalle de importación", "error");
    }
  };

  // Cuando cambie la importación seleccionada, cargar su detalle
  useEffect(() => {
    if (!selectedImportacionId) return;
    fetchImportacionDetalle(selectedImportacionId);
  }, [selectedImportacionId]);

  // UseEffect para cargar comentarios cuando se abre el modal
  useEffect(() => {
    if (isComentariosModalOpen && comentarioModalRow) {
      fetchComentarios();
    }
  }, [isComentariosModalOpen, comentarioModalRow]);

  // UseEffect para actualizar el texto de archivos seleccionados
  useEffect(() => {
    if (comentarioModalFiles.length === 0) {
      setComentarioModalInputFileText("Selecciona un archivo");
    } else {
      setComentarioModalInputFileText(
        `${comentarioModalFiles.length} ${
          comentarioModalFiles.length > 1
            ? "archivos seleccionados"
            : "archivo seleccionado"
        }`
      );
    }
  }, [comentarioModalFiles]);

  // UseEffect para generar opciones de filtros
  useEffect(() => {
    if (!Array.isArray(comparativaGOI) || comparativaGOI.length === 0) {
      setUnidades([]);
      setFechasImportacion([]);
      return;
    }
    
    // Extraer unidades únicas
    const allUnidades = comparativaGOI
      .map((item) => item.unidad)
      .filter((value, index, self) => 
        value != null && 
        value !== undefined && 
        value !== '' && 
        self.indexOf(value) === index
      );

    // Extraer fechas de importación únicas (solo fecha, sin hora)
    const allFechasImportacion = comparativaGOI
      .map((item) => {
        if (item.fechaImportacion) {
          try {
            return new Date(item.fechaImportacion).toLocaleDateString('es-ES');
          } catch (e) {
            return null;
          }
        }
        return null;
      })
      .filter((value, index, self) => 
        value != null && 
        value !== undefined && 
        value !== '' && 
        self.indexOf(value) === index
      );

    setUnidades(allUnidades);
    setFechasImportacion(allFechasImportacion);
  }, [comparativaGOI]);

  const handleSubmitImportModal = async () => {
    if (!nombreTarifaGoi.trim()) {
      notificationRef.current?.addNotification("Nombre tarifa GOI requerido", "warning");
      return;
    }
    if (!selectedTarifaBook) {
      notificationRef.current?.addNotification("Seleccione una tarifa Book", "warning");
      return;
    }
    if (!importFile) {
      notificationRef.current?.addNotification("Seleccione un fichero Excel", "warning");
      return;
    }

    setIsImportSubmitting(true);
    try {
      const api = getApiInstance();
      const tarifaBookParam =
        typeof selectedTarifaBook === "string"
          ? selectedTarifaBook
          : selectedTarifaBook?.value ?? selectedTarifaBook?.label ?? "";
      const params = `?nombreTarifaGoi=${encodeURIComponent(nombreTarifaGoi)}&nombreTarifaBook=${encodeURIComponent(tarifaBookParam)}`;
      const formData = new FormData();
      formData.append("archivoExcel", importFile);

      // Ya no se envía codTienda
      const response = await api.post(`/api/GOI/ImportarTarifaProveedor${params}`, formData);
      const data = response.data || {};
      // Si hay referencias duplicadas, mostrar popup de confirmación
      if (Array.isArray(data.referenciasDuplicadas) && data.referenciasDuplicadas.length > 0) {
        setImportConfirmData({
          idImportacion: data.idImportacion,
          referenciasDuplicadas: data.referenciasDuplicadas,
        });
        setIsConfirmPopupOpen(true);
      } else {
        // Si no hay duplicadas, continuar como antes
        setComparativaGOI(Array.isArray(data.resumen) ? data.resumen : []);
        setFilteredComparativaGOI(Array.isArray(data.resumen) ? data.resumen : []);
        await fetchImportaciones();
        if (data.idImportacion) {
          setSelectedImportacionId(data.idImportacion);
          await fetchImportacionDetalle(data.idImportacion);
        }
        
        // Enviar notificación de importación exitosa
        await fetchSendNotificationImportacion(nombreTarifaGoi);
        
        setIsImportModalOpen(false);
        setNombreTarifaGoi("");
        setSelectedTarifaBook(null);
        setImportFile(null);
        notificationRef.current?.addNotification("Muebles importados correctamente", "success");
      }
    } catch (error) {
      const mensaje = error.response?.data?.mensaje || "Error al importar tarifas desde Excel.";
      // Detectar si hay referenciasDuplicadasExcel
      if (error.response?.data?.referenciasDuplicadasExcel) {
        setDuplicadasModalData(error.response.data.referenciasDuplicadasExcel);
        setDuplicadasModalOpen(true);
      }
      notificationRef.current?.addNotification(mensaje, "error");
    } finally {
      setIsImportSubmitting(false);
    }
  };

  // Función para manejar la decisión de actualizar (antes sobrescribir)
  const handleActualizar = async () => {
    await handleDecisionImportacion("actualizar");
    setIsConfirmPopupOpen(false);
  };

  // Función para manejar la decisión de añadir solo nuevas
  const handleSoloNuevas = async () => {
    await handleDecisionImportacion("solonuevas");
    setIsConfirmPopupOpen(false);
  };

  const handleSobrescribirTarifa = async () => {
    await handleDecisionImportacion("sobrescribirtarifa");
    setIsConfirmPopupOpen(false);
  };

  // Nuevo: función para aplicar decisión del usuario
  const handleDecisionImportacion = async (modo) => {
    if (!importConfirmData?.idImportacion) return;
    try {
      const api = getApiInstance();
      await api.post("/api/GOI/AplicarDecisionImportacionTarifa", {
        IdImportacion: importConfirmData.idImportacion,
        Modo: modo,
      });
      // Recargar datos tras aplicar decisión
      await fetchImportaciones();
      setSelectedImportacionId(importConfirmData.idImportacion);
      await fetchImportacionDetalle(importConfirmData.idImportacion);
      
      // Enviar notificación de importación (usar el nombreTarifaGoi guardado)
      const nombreTarifaImportada = nombreTarifaGoi || "Tarifa GOI";
      await fetchSendNotificationImportacion(nombreTarifaImportada);
      
      setIsImportModalOpen(false);
      setNombreTarifaGoi("");
      setSelectedTarifaBook(null);
      setImportFile(null);
      setImportConfirmData(null);
      notificationRef.current?.addNotification("Decisión aplicada correctamente", "success");
    } catch (error) {
      notificationRef.current?.addNotification("Error al aplicar decisión", "error");
    }
  };

  // Funciones del sistema de comentarios
  const fetchComentarios = async () => {
    const api = getApiInstance();
    try {
      const response = await api.get(
        `/api/GOI/GetAllComentarios/${comentarioModalRow.idDetalle}`
      );

      if (response.status !== 200) {
        notificationRef.current?.addNotification(
          "Error cargando comentarios.",
          "error"
        );
        return;
      }

      const comentarios = response.data.map((com) => ({
        codComentario: com.codComentario,
        comentario: com.comentario,
        nombreUsuario:
          com.nombreUsuario == user.nombre ? "Tú" : com.nombreUsuario,
        fecha: formatDateComment(com.fecha),
        rutasArchivos: com.rutasArchivos || [],
      }));

      // Actualiza el estado de comentarios por fila
      setComentariosPorFila((prev) => {
        const id = comentarioModalRow.idDetalle;
        const prevList = prev[id] ?? [];

        // Índice por codComentario para mezclar sin duplicar
        const map = new Map(prevList.map((c) => [c.codComentario, c]));

        for (const c of comentarios) {
          // Si ya existe, actualízalo; sino, añádelo
          map.set(c.codComentario, {
            ...(map.get(c.codComentario) ?? {}),
            ...c,
          });
        }

        const merged = Array.from(map.values());

        return {
          ...prev,
          [id]: merged,
        };
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSendNotification = async (comentario) => {
    try {
      const api = getApiInstance();

      const formData = new FormData();

      // Obtener información detallada para la notificación
      const referencia = comentarioModalRow?.referencia ?? 
                        comentarioModalRow?.Referencia ?? 
                        "Sin referencia";
      
      // Buscar la tarifa GOI desde las importaciones cargadas
      const importacionActual = importaciones.find(imp => imp.IdImportacion === selectedImportacionId);
      
      const tarifaGoi = importacionActual?.NombreTarifaGoi ?? 
                       importacionActual?.nombreTarifaGoi ?? 
                       "Tarifa GOI no especificada";

      // Crear mensaje detallado para admin Atlante
      const mensajeDetallado = `Comentario: "${comentario.comentario}" | Tarifa GOI: ${tarifaGoi}`;

      // Tipos destinatarios
      // None = 0,
      // Admin = 1,
      // Todos = 2,
      // Personalizado = 3
      // AdminProveedores = 4

      formData.append("DestinatariosNotificacion", 4);
      formData.append("CodUsuarioNotifica", user.codUsuario);
      formData.append("CodUsuarioExcluir", user.codUsuario); // Excluir al usuario que envía el comentario
      formData.append("Mensaje", mensajeDetallado);
      formData.append("Titulo", `Nuevo comentario en validación GOI - ${referencia}`);
      
      // Tipos notificación (sin aplicación en el front por ahora)
      // Info = 0,
      // Warning = 1,
      // Error = 2

      formData.append("TipoNotificacion", 0);
      
      // Las notificaciones van a administradores, siempre dirigir a la vista de admin
      const enlace = selectedImportacionId 
        ? `/admin/gestion-tarifas?activeTab=validacionGOI&importacionId=${selectedImportacionId}&referencia=${encodeURIComponent(referencia)}`
        : `/admin/gestion-tarifas?activeTab=validacionGOI`;
      formData.append("Enlace", enlace);

      let response = await api.post(`/api/Notificaciones`, formData);
    } catch (error) {
      console.error("Error enviando notificación:", error);
    }
  };

  const fetchSendNotificationImportacion = async (nombreTarifaGoi) => {
    try {
      const api = getApiInstance();
      const formData = new FormData();

      // Mapeo de códigos de empresa a nombres de proveedor
      const empresaNombres = {
        1: "Grupo Atlante",
        2: "Bershka Imagen",
        3: "Bershka Obras",
        4: "Castel",
        5: "Culdesac",
        6: "Dimalnox",
        7: "Kimak",
        8: "Malasa Mexico",
        9: "Malasa",
        87: "TestEmpresa",
      };

      // Obtener nombre del proveedor
      const codEmpresa = user?.codEmpresa ?? user?.CodEmpresa ?? "";
      const nombreProveedor = empresaNombres[codEmpresa] || "Proveedor desconocido";

      // Crear título y mensaje de la notificación
      const titulo = `Nueva tarifa GOI importada por ${nombreProveedor}`;
      const mensaje = `Nombre Tarifa GOI: ${nombreTarifaGoi}`;

      // Enviar notificación a administradores (tipo 1 = Admin)
      formData.append("DestinatariosNotificacion", 1);
      formData.append("CodUsuarioNotifica", user.codUsuario);
      formData.append("Mensaje", mensaje);
      formData.append("Titulo", titulo);
      formData.append("TipoNotificacion", 0); // Info
      
      // Enlace para navegar a la validación GOI
      const enlace = "/admin/gestion-tarifas?activeTab=validacionGOI";
      formData.append("Enlace", enlace);

      await api.post(`/api/Notificaciones`, formData);
    } catch (error) {
      console.error("Error enviando notificación de importación:", error);
    }
  };

  const handleCloseComentariosModal = () => {
    setIsComentariosModalOpen(false);
    setComentarioModalRow(null);
    setComentarioModalInput("");
    setComentarioModalFiles([]);
  };

  // Manejar archivos en el modal
  const handleComentarioModalFiles = (event) => {
    const filesArray = Array.from(event.target.files);
    setComentarioModalFiles((prevFiles) => [
      ...prevFiles,
      ...filesArray.filter(
        (file) => !prevFiles.some((prevFile) => prevFile.name === file.name)
      ),
    ]);
  };

  // Envío de comentario
  const handleEnviarComentarioModal = async () => {
    const api = getApiInstance();
    if (!comentarioModalInput.trim() || !comentarioModalRow) {
      return;
    }
    try {
      const fecha = new Date();
      const formData = new FormData();

      formData.append("CodMueble", comentarioModalRow.idDetalle);
      formData.append("Comentario", comentarioModalInput);
      formData.append("Fecha", fecha.toLocaleString());
      comentarioModalFiles.forEach((file) => {
        formData.append("RutasArchivos", file);
      });

      // Usa el endpoint de comentarios GOI
      const response = await api.post(
        `/api/GOI/Comentario`,
        formData
      );

      if (response.status === 200) {
        notificationRef.current?.addNotification(
          "Comentario creado correctamente.",
          "success"
        );

        const nuevoComentario = {
          codComentario: response.data.codComentario,
          comentario: comentarioModalInput,
          nombreUsuario: "Tú",
          fecha: formatDateComment(fecha.toISOString()),
          rutasArchivos: comentarioModalFiles,
        };

        setComentariosPorFila((prev) => ({
          ...prev,
          [comentarioModalRow.idDetalle]: [
            ...(prev[comentarioModalRow.idDetalle] || []),
            nuevoComentario,
          ],
        }));
        setComentarioModalInput("");
        setComentarioModalFiles([]);

        // Enviar notificación
        await fetchSendNotification(nuevoComentario);

      } else {
        notificationRef.current?.addNotification(
          "Hubo un error al guardar el comentario.",
          "error"
        );
      }
    } catch (error) {
      notificationRef.current?.addNotification(
        "Hubo un error al guardar el comentario.",
        "error"
      );
    }
  };
    const handleSearch = (query) => {
      setSearchTerm(query);
    };

  // Filtrar datos con los filtros aplicados
  const filteredData = useMemo(() => {
    if (!Array.isArray(comparativaGOI)) {
      return [];
    }
    
    let filtered = comparativaGOI.filter((item) => {
      // Filtro por término de búsqueda
      const matchesSearch = searchTerm === '' || Object.values(item).some((value) =>
        (value !== null && value !== undefined ? value.toString() : "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
      
      return matchesSearch;
    });

    // Aplicar filtro de estado
    if (filterEstado.length > 0) {
      filtered = filtered.filter((item) =>
        filterEstado.some(estado => estado.id === item.estado)
      );
    }

    // Aplicar filtro de unidad
    if (filterUnidad.length > 0) {
      filtered = filtered.filter((item) => {
        const itemUnidad = item.unidad;
        return filterUnidad.includes(itemUnidad);
      });
    }

    // Aplicar filtro de fecha de importación
    if (filterFechaImportacion.length > 0) {
      filtered = filtered.filter((item) => {
        if (item.fechaImportacion) {
          try {
            const fechaItem = new Date(item.fechaImportacion).toLocaleDateString('es-ES');
            return filterFechaImportacion.includes(fechaItem);
          } catch (e) {
            console.error('Error parseando fecha:', item.fechaImportacion);
            return false;
          }
        }
        return false;
      });
    }

    return filtered;
  }, [
    searchTerm,
    comparativaGOI,
    filterEstado,
    filterUnidad,
    filterFechaImportacion,
  ]);

  // Handlers para filtros
  const handleSelectEstado = (estado) => {
    setFilterEstado((prev) => {
      const isSelected = prev.some(item => item.id === estado.id);
      if (isSelected) {
        return prev.filter(item => item.id !== estado.id);
      } else {
        return [...prev, estado];
      }
    });
  };

  const handleSelectUnidad = (unidadName) => {
    setFilterUnidad((prevSelected) => {
      if (prevSelected.includes(unidadName)) {
        return prevSelected.filter(
          (unidad) => unidad !== unidadName
        );
      } else {
        return [...prevSelected, unidadName];
      }
    });
  };

  const handleSelectFechaImportacion = (fechaName) => {
    setFilterFechaImportacion((prevSelected) => {
      if (prevSelected.includes(fechaName)) {
        return prevSelected.filter(
          (fecha) => fecha !== fechaName
        );
      } else {
        return [...prevSelected, fechaName];
      }
    });
  };

  const handleCleanFilter = () => {
    setFilterEstado([]);
    setFilterUnidad([]);
    setFilterFechaImportacion([]);
  };

      const handleExportExcel = async () => {
        try {
          if (selectedComparativaGOI.length === 0) {
            notificationRef.current.addNotification(
              "No hay solicitudes seleccionadas para exportar.",
              "warning"
            );
            return;
          }
          
          if (!selectedImportacionId) {
            notificationRef.current.addNotification(
              "No hay importación seleccionada.",
              "warning"
            );
            return;
          }
          
          // Convertir IDs seleccionados a objetos completos
          const selectedObjects = filteredData.filter(item => 
            selectedComparativaGOI.includes(item.idDetalle)
          );
          
          if (selectedObjects.length === 0) {
            notificationRef.current.addNotification(
              "Error: No se pudieron encontrar los datos seleccionados.",
              "error"
            );
            return;
          }
          
          const api = getApiInstance();
          
          // El endpoint espera directamente un array de IDs (enteros)
          const selectedIds = selectedObjects.map(obj => obj.idDetalle);
          
          const response = await api.post(
            `/api/GOI/ExportarGOI/${selectedImportacionId}`,
            selectedIds,  // Enviar directamente el array, no un objeto
            { responseType: "blob" }
          );
    
          if (response.status !== 200) {
            throw new Error("Error al descargar el archivo");
          }
    
          const blob = await response.data;
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
    
          let fileName = "Validaciones_GOI.xlsx";
          const disposition = response.headers.get("Content-Disposition");
          if (disposition && disposition.indexOf("filename=") !== -1) {
            const fileNameMatch = disposition.match(/filename="?([^"]+)"?/);
            if (fileNameMatch && fileNameMatch.length > 1) {
              fileName = fileNameMatch[1];
            }
          }
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
    
          notificationRef.current.addNotification(
            "Archivo descargado correctamente.",
            "success"
          );
        } catch (error) {
          notificationRef.current.addNotification(
            "Error al descargar el archivo.",
            "error"
          );
          console.error("Error al descargar el archivo:", error);
        }
      };

  return (
    <section>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12, alignItems: "center" }}>
        <InputSearch
          context="tableSearch"
          placeholder="Buscar..."
          onSearch={handleSearch}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 520 }}>
          <div style={{ flex: 1 }}>
            <InputDropdown
              label={null} // evitar label interno que se muestra por encima
              placeholder="Selecciona una tarifa importada"
              style={{ fontSize: 15 }} // si el componente lo admite
              options={importaciones.map((imp) => ({
                label: imp.NombreTarifaGoi ?? imp.ArchivoNombre ?? `Importación ${imp.IdImportacion}`,
                value: imp.IdImportacion,
              }))}
              onSelect={(val) => {
                const raw = Array.isArray(val) ? (val.length ? val[0] : null) : val;
                const id = raw == null ? null : typeof raw === "number" ? raw : parseInt(raw.value ?? raw, 10);
                setSelectedImportacionId(id);
              }}
              withCheckboxes={false}
              hasSearch={true}
            />
          </div>
        </div>
        <Button
          variant="outline"
          label="Filtros"
          icon={<FiFilter />}
          onClick={() => setIsFiltersModalOpen(true)}
        />
        <Button label="Importar" variant="outline" icon={<BiImport />} onClick={() => setIsImportModalOpen(true)} />
        <Button
                      label="Exportar"
                      variant="outline"
                      icon={<BiExport />}
                      onClick={handleExportExcel}
                      disabled={selectedComparativaGOI.length === 0}
                    />
      </div>

      <div>
        <TableEditable
          data={filteredData}
          columns={columnsProveedor}
          rowKey="idDetalle"
          showHeaderCheckbox={true}
          onRowSelect={setSelectedComparativaGOI}
          selectedRows={selectedComparativaGOI}
          notificationRef={notificationRef}
          noResultsMessage="No hay datos importados."
          statesRestrictEdit={[]} 
        />
      </div>

      <Modal
        title="Importar Tarifas GOI"
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onAccept={handleSubmitImportModal}
        outlineButtonLabel="Cancelar"
        primaryButtonLabel={isImportSubmitting ? "Importando..." : "Importar"}
      >
        <div className="modal-inputs">
          <div className="section-header__modal__section">
            <InputDropdownAdd
              options={tarifasGoiExistentes}
              value={nombreTarifaGoi}
              onSelect={(e) => handleSetNombreTarifaGoi(e.value, "select")}
              onAddOption={(e) => handleSetNombreTarifaGoi(e, "add")}
              placeholder="Nombre Tarifa GOI"
              label="Nombre tarifa GOI"
              required
              addDateAutomatically={false}
            />
          </div>

          <div className="section-header__modal__section">
            <InputDropdown
              label="Tarifa Book"
              options={tarifasProveedorOptions}
              onSelect={(val) => {
                const raw = Array.isArray(val) ? (val.length ? val[0] : null) : val;
                const normalized = raw == null ? null : typeof raw === "string" ? raw : raw.value ?? raw.label ?? null;
                setSelectedTarifaBook(normalized);
              }}
              withCheckboxes={false}
              hasSearch={true}
            />
          </div>

          {/* Eliminado: selección de tienda, ya no es necesaria */}

          <div className="section-header__modal__section">
            <label>Archivo Excel</label>

            {!importFile && (
            <Input
              type="file"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            />
            )
          }
            <FileList
              files={importFile ? [importFile] : []}
              handleDelete={() => setImportFile(null)}
            />
          </div>
        </div>
      </Modal>

      {/* Popup personalizado para decisión de referencias duplicadas */}
      {isConfirmPopupOpen && (
        <div className="popup-overlay-custom" onClick={() => setIsConfirmPopupOpen(false)}>
          <div className="popup-decision-custom" onClick={(e) => e.stopPropagation()}>
            <div className="popup-decision-custom__header">
              <div className="popup-decision-custom__title">
                <PiWarning className="popup-decision-custom__icon" />
                <h3>Referencias duplicadas</h3>
              </div>
              <button
                className="popup-decision-custom__close"
                onClick={() => setIsConfirmPopupOpen(false)}
              >
                <IoMdClose />
              </button>
            </div>

            <div className="popup-decision-custom__content">
              <p className="popup-decision-custom__message">
                Se han encontrado referencias que ya estaban cargadas.
                <br /><br />
                <b>Referencias detectadas:</b>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: 18, fontSize: '1rem', color: '#b26a00' }}>
                  {importConfirmData?.referenciasDuplicadas?.map((ref) => (
                    <li key={ref} style={{ marginBottom: 2 }}>{ref}</li>
                  ))}
                </ul>
                <br />¿Qué acción deseas realizar?
              </p>
            </div>

            <div className="popup-decision-custom__actions">
              <Button
                label="Cancelar"
                variant="outline"
                onClick={() => setIsConfirmPopupOpen(false)}
                fullWidth="width-100"
              />
              <Button
                label="Añadir solo nuevas"
                variant="filled"
                onClick={handleSoloNuevas}
                fullWidth="width-100"
                statusType="success"
              />
              <Button
                label="Actualizar referencias existentes y añadir nuevas"
                variant="filled"
                onClick={handleActualizar}
                fullWidth="width-100"
                statusType="info"
              />
              <Button
                label="Sobrescribir tarifa -las referencias ya aprobadas no se pueden eliminar-"
                variant="filled"
                onClick={handleSobrescribirTarifa}
                fullWidth="width-100"
                statusType="error"
              />
            </div>
          </div>
        </div>
      )}

      {duplicadasModalOpen && (
  <Modal
    title="Referencias duplicadas en el Excel"
    isOpen={duplicadasModalOpen}
    onClose={() => setDuplicadasModalOpen(false)}
    outlineButtonLabel="Cerrar"
    hideFooter={true}
  >
    <div>
      <p
        style={{
          marginBottom: 16,
          fontSize: "1.1rem",
          color: "#b26a00",
          fontWeight: 500,
          background: "#fffbe6",
          borderRadius: 6,
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <PiWarning style={{ fontSize: 22, marginRight: 8, color: "#b26a00" }} />
        El archivo contiene referencias duplicadas. Revisa el Excel antes de volver a importarlo.
      </p>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", borderBottom: "1px solid #eee", padding: "4px 8px" }}>Referencia</th>
            <th style={{ textAlign: "left", borderBottom: "1px solid #eee", padding: "4px 8px" }}>Repeticiones</th>
          </tr>
        </thead>
        <tbody>
          {duplicadasModalData.map((item) => (
            <tr key={item.referencia}>
              <td style={{ padding: "4px 8px" }}>{item.referencia}</td>
              <td style={{ padding: "4px 8px" }}>{item.repeticiones}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Modal>
)}

      {/* Modal de filtros */}
      <Modal
        title="Filtros"
        isOpen={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        onAccept={() => setIsFiltersModalOpen(false)}
        outlineButtonLabel="Limpiar"
        primaryButtonLabel={`Ver resultados (${filteredData?.length || 0})`}
        onCancel={handleCleanFilter}
      >
        <div className="modal-inputs">
          <div className="section-header__modal__section">
            <Accordion title="Estado">
              <ul className="section-header__modal__section__content">
                {estados.map((estado) => (
                  <Tag
                    key={estado.id}
                    label={estado.label}
                    isSelected={filterEstado.some(e => e.id === estado.id)}
                    onSelect={() => handleSelectEstado(estado)}
                  />
                ))}
              </ul>
            </Accordion>
          </div>

          <div className="section-header__modal__section">
            <Accordion title="Unidad">
              <ul className="section-header__modal__section__content">
                {unidades.map((unidad, index) => (
                  <Tag
                    key={index}
                    label={unidad}
                    isSelected={filterUnidad.includes(unidad)}
                    onSelect={() => handleSelectUnidad(unidad)}
                  />
                ))}
              </ul>
            </Accordion>
          </div>

          <div className="section-header__modal__section">
            <Accordion title="Fecha Importación">
              <ul className="section-header__modal__section__content">
                {fechasImportacion.map((fecha, index) => (
                  <Tag
                    key={index}
                    label={fecha}
                    isSelected={filterFechaImportacion.includes(fecha)}
                    onSelect={() => handleSelectFechaImportacion(fecha)}
                  />
                ))}
              </ul>
            </Accordion>
          </div>
        </div>
      </Modal>

      {/* Modal de comentarios */}
      <Modal
        title={`Comentarios de ${comentarioModalRow?.referencia || ""}`}
        isOpen={isComentariosModalOpen}
        onClose={handleCloseComentariosModal}
        onCancel={handleCloseComentariosModal}
        hideFooter={true}
        className="modal--centered"
      >
        <div className="detalle-mueble__info__comments">
          <div className="detalle-mueble__info__comments__comments-list">
            {comentarioModalRow &&
            (comentariosPorFila[comentarioModalRow.idDetalle] || []).length >
              0 ? (
              <>
                {(comentariosPorFila[comentarioModalRow.idDetalle] || []).map(
                  (com, idx) => (
                    <div
                      key={com.codComentario}
                      className="detalle-mueble__info__comments__comments-list__item"
                    >
                      <div className="detalle-mueble__info__comments__comments-list__item__info">
                        <div className="detalle-mueble__info__comments__comments-list__item__author">
                          <div className="avatar">
                            <div className="avatar__inner">
                              {com.nombreUsuario.charAt(0).toUpperCase()}
                            </div>
                          </div>
                          <p>{com.nombreUsuario}</p>
                          {com.fecha && (
                            <span style={{ marginLeft: 8, color: "#888" }}>
                              ({com.fecha})
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="detalle-mueble__info__comments__comments-list__item__comment">
                        {com.comentario || (
                          <span style={{ color: "#aaa" }}>Sin comentario</span>
                        )}
                      </p>
                      {com.rutasArchivos && com.rutasArchivos.length > 0 && (
                        <FileList files={com.rutasArchivos} filesVisible={3} />
                      )}
                    </div>
                  )
                )}
              </>
            ) : (
              <p
                className="detalle-mueble__info__comments__comments-list__item__comment"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "12px 0",
                  fontWeight: "700",
                }}
              >
                Todavía no hay comentarios en este mueble.
              </p>
            )}
          </div>
          {/* Formulario para agregar comentario */}
          <div className="detalle-mueble__info__comments__add-comment">
            <InputTextfield
              label="Escribe tu comentario"
              value={comentarioModalInput}
              onChange={(e) => setComentarioModalInput(e.target.value)}
              placeholder="Escribe aquí tu comentario..."
            />
            <Input
              type="file"
              label="Archivo adjunto"
              name="archivoAdjunto"
              placeholder={comentarioModalInputFileText}
              multipleFiles={true}
              onChange={handleComentarioModalFiles}
            />
            {comentarioModalFiles.length > 0 && (
              <FileList
                files={comentarioModalFiles}
                handleDelete={(file) =>
                  setComentarioModalFiles((prev) =>
                    prev.filter((f) => f.name !== file.name)
                  )
                }
              />
            )}
            <Button
              label="Enviar comentario"
              variant="filled"
              onClick={handleEnviarComentarioModal}
            />
          </div>
        </div>
      </Modal>

      <Notification ref={notificationRef} />
    </section>
  );
}